import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

export const PRIMARY_FONT = '"Aptos", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
export const MONO_FONT = '"SFMono-Regular", "Roboto Mono", Consolas, monospace';
export const SURFACE_RADIUS = '16px';
export const INNER_RADIUS = '12px';
export const BADGE_RADIUS = '10px';

export const DensityContext = React.createContext({
    compact: false,
    phone: false,
    sectionGap: 2,
    panelPadding: 2,
});

export const useDensity = () => React.useContext(DensityContext);

export const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return '—';
    }
    const formatted = Math.abs(number).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return number < 0 ? `₹ -${formatted}` : `₹ ${formatted}`;
};

export const formatNumber = (value, digits = 2) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return '—';
    }
    return number.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });
};

export const formatRelativeTime = (value) => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (diffSeconds < 60) {
        return `${diffSeconds}s ago`;
    }
    if (diffSeconds < 3600) {
        return `${Math.floor(diffSeconds / 60)}m ago`;
    }
    return `${Math.floor(diffSeconds / 3600)}h ago`;
};

export const formatTimestamp = (value) => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

export const formatLatency = (value, digits = 1) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return '—';
    }
    if (number >= 1000) {
        return `${(number / 1000).toFixed(digits)}s`;
    }
    return `${number.toFixed(digits)}ms`;
};

export const formatChipText = (value) => (value || '').toString().replace(/_/g, ' ');

export const toneForStatus = (status) => {
    const value = (status || '').toString().toLowerCase();
    if (['active', 'live', 'operational', 'connected', 'strong', 'success', 'running', 'healthy'].includes(value)) {
        return 'success';
    }
    if (['info', 'synced', 'ready'].includes(value)) {
        return 'primary';
    }
    if (['warning', 'cautious', 'armed', 'waiting_reentry', 'waiting_spot', 'partial', 'lagging', 'stale', 'loading', 'waiting'].includes(value)) {
        return 'warning';
    }
    if (['error', 'failed', 'halted', 'fatal', 'missing', 'disconnected', 'stopped'].includes(value)) {
        return 'error';
    }
    return 'default';
};

export const accentColorFor = (theme, accent = 'neutral') => {
    switch (accent) {
        case 'strategy':
            return theme.palette.success.main;
        case 'platform':
            return theme.palette.primary.main;
        case 'success':
            return theme.palette.success.main;
        case 'warning':
            return theme.palette.warning.main;
        case 'error':
            return theme.palette.error.main;
        case 'primary':
            return theme.palette.primary.main;
        default:
            return theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[700];
    }
};

export const getValueStyles = (theme, tone = 'default', large = false) => ({
    color: tone === 'default' ? theme.palette.text.primary : accentColorFor(theme, tone),
    fontFamily: PRIMARY_FONT,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: large ? '-0.05em' : '-0.025em',
    lineHeight: 1.08,
});

export const getSurfaceStyles = (theme, accent = 'neutral', options = {}) => {
    const { withTopBar = true, muted = false } = options;
    const accentColor = accentColorFor(theme, accent);
    const isDark = theme.palette.mode === 'dark';

    return {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: SURFACE_RADIUS,
        border: '1px solid',
        borderColor: alpha(accentColor, isDark ? 0.32 : 0.16),
        backgroundColor: muted
            ? (isDark ? alpha('#08111f', 0.66) : alpha('#f8fafc', 0.78))
            : (isDark ? alpha('#08111f', 0.78) : alpha('#ffffff', 0.92)),
        boxShadow: isDark
            ? '0 18px 48px rgba(2, 6, 23, 0.34)'
            : '0 16px 40px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        ...(withTopBar
            ? {
                  '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: '0 0 auto 0',
                      height: 2,
                      backgroundColor: alpha(accentColor, isDark ? 0.92 : 1),
                  },
              }
            : {}),
    };
};

export const eyebrowSx = {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'text.secondary',
};

export const StatusBadge = ({ status, label, tone, size = 'small' }) => {
    const { compact, phone } = useDensity();
    const resolvedTone = tone || toneForStatus(status);
    const resolvedLabel = label || formatChipText(status || 'unknown');
    const resolvedSize = size === 'small' ? (compact && !phone ? 'dense' : 'small') : size;

    return (
        <Chip
            label={resolvedLabel}
            size={resolvedSize === 'dense' ? 'small' : resolvedSize}
            variant="outlined"
            sx={(theme) => {
                const chipColor = accentColorFor(theme, resolvedTone === 'default' ? 'neutral' : resolvedTone);
                return {
                    height: resolvedSize === 'dense' ? 18 : resolvedSize === 'small' ? 20 : 26,
                    borderRadius: BADGE_RADIUS,
                    color: resolvedTone === 'default' ? theme.palette.text.primary : chipColor,
                    borderColor: alpha(chipColor, theme.palette.mode === 'dark' ? 0.42 : 0.24),
                    backgroundColor: alpha(chipColor, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                    fontWeight: 700,
                    fontSize: resolvedSize === 'dense' ? '0.64rem' : resolvedSize === 'small' ? '0.7rem' : '0.76rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    '& .MuiChip-label': {
                        px: resolvedSize === 'dense' ? 0.75 : 0.95,
                    },
                };
            }}
        />
    );
};

export const MetricLabel = ({ label, description }) => (
    <Stack direction="row" spacing={0.4} alignItems="center">
        <Typography sx={eyebrowSx}>
            {label}
        </Typography>
        {description ? (
            <Tooltip
                title={description}
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                placement="top"
                slotProps={{
                    tooltip: {
                        sx: {
                            maxWidth: 280,
                            fontSize: '0.74rem',
                            lineHeight: 1.55,
                        },
                    },
                }}
            >
                <IconButton
                    size="small"
                    sx={{
                        p: 0,
                        width: 18,
                        height: 18,
                        color: 'text.disabled',
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
        ) : null}
    </Stack>
);

export const MetricMatrix = ({ metrics, columns = { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' } }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                gap: compact ? 1 : 1.25,
            }}
        >
            {metrics.map((metric) => (
                <Box
                    key={metric.label}
                    sx={(theme) => ({
                        borderRadius: INNER_RADIUS,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.82),
                        backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#0f172a', 0.58)
                            : alpha('#ffffff', 0.88),
                        px: compact ? 1 : 1.2,
                        py: compact ? 0.9 : 1.05,
                        minWidth: 0,
                    })}
                >
                    <MetricLabel label={metric.label} description={metric.description} />
                    <Typography
                        variant="body1"
                        sx={(theme) => ({
                            ...getValueStyles(theme, metric.tone || 'default'),
                            mt: compact ? 0.55 : 0.7,
                            fontSize: compact ? '0.9rem' : '0.95rem',
                        })}
                    >
                        {metric.value}
                    </Typography>
                    {metric.detail ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.45, display: 'block', lineHeight: 1.4 }}
                        >
                            {metric.detail}
                        </Typography>
                    ) : null}
                </Box>
            ))}
        </Box>
    );
};

export const ToneBanner = ({ tone = 'default', label, message }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => {
                const resolvedTone = tone === 'info' ? 'primary' : tone;
                const color = accentColorFor(theme, resolvedTone === 'default' ? 'neutral' : resolvedTone);

                return {
                    borderRadius: INNER_RADIUS,
                    border: '1px solid',
                    borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.42 : 0.22),
                    backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.08),
                    px: compact ? 1.25 : 1.5,
                    py: compact ? 1 : 1.2,
                };
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.8}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Typography
                    sx={(theme) => ({
                        ...eyebrowSx,
                        color: accentColorFor(theme, tone === 'default' ? 'neutral' : tone === 'info' ? 'primary' : tone),
                    })}
                >
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                    {message}
                </Typography>
            </Stack>
        </Box>
    );
};

export const SectionFrame = ({ accent, eyebrow, title, description, status, children }) => {
    const { compact, sectionGap } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                ...getSurfaceStyles(theme, accent, { muted: true }),
                p: { xs: compact ? 1.5 : 1.85, md: compact ? 1.85 : 2.2 },
                backgroundColor: theme.palette.mode === 'dark'
                    ? alpha('#020617', 0.72)
                    : alpha('#f8fafc', 0.86),
            })}
        >
            <Stack spacing={sectionGap}>
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        <Typography sx={eyebrowSx}>{eyebrow}</Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                mt: 0.55,
                                fontWeight: 700,
                                letterSpacing: '-0.03em',
                                fontFamily: PRIMARY_FONT,
                                fontSize: { xs: '1.2rem', md: '1.35rem' },
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.55, maxWidth: 880, lineHeight: 1.6 }}
                        >
                            {description}
                        </Typography>
                    </Box>
                    {status ? (
                        <Box sx={{ pt: { lg: 0.15 }, alignSelf: 'flex-start' }}>
                            {status}
                        </Box>
                    ) : null}
                </Stack>
                {children}
            </Stack>
        </Box>
    );
};

export const PanelFrame = ({ accent = 'neutral', eyebrow, title, description, action, children, minHeight }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                ...getSurfaceStyles(theme, accent),
                p: compact ? 1.5 : 1.85,
                minHeight,
            })}
        >
            <Stack spacing={compact ? 1.2 : 1.5} sx={{ height: '100%' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={0.9}
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        {eyebrow ? <Typography sx={eyebrowSx}>{eyebrow}</Typography> : null}
                        <Typography
                            variant="h6"
                            sx={{
                                mt: eyebrow ? 0.4 : 0,
                                fontWeight: 700,
                                letterSpacing: '-0.025em',
                                fontFamily: PRIMARY_FONT,
                                fontSize: compact ? '1rem' : '1.1rem',
                            }}
                        >
                            {title}
                        </Typography>
                        {description ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.35, lineHeight: 1.55 }}
                            >
                                {description}
                            </Typography>
                        ) : null}
                    </Box>
                    {action ? (
                        <Box sx={{ pt: { sm: 0.1 }, alignSelf: 'flex-start' }}>
                            {action}
                        </Box>
                    ) : null}
                </Stack>
                {children}
            </Stack>
        </Box>
    );
};

const renderColumnValue = (column, row) =>
    column.render ? column.render(row[column.key], row) : row[column.key] ?? '—';

export const DataTable = ({ title, description, columns, rows, emptyMessage, accent = 'neutral', framed = true }) => {
    const { compact, phone } = useDensity();
    const content = rows.length ? (
        phone ? (
            <Stack spacing={1}>
                {rows.map((row, index) => {
                    const [titleColumn, ...detailColumns] = columns;
                    const titleValue = renderColumnValue(titleColumn, row);

                    return (
                        <Box
                            key={`${title}-${index}`}
                            sx={(theme) => ({
                                borderRadius: INNER_RADIUS,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.divider, 0.85),
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha('#0f172a', 0.62)
                                    : alpha('#ffffff', 0.92),
                                px: 1.15,
                                py: 1.05,
                            })}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {titleValue}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    mt: 0.95,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                    gap: 0.85,
                                }}
                            >
                                {detailColumns.map((column) => (
                                    <Box key={column.key} sx={{ minWidth: 0 }}>
                                        <Typography sx={eyebrowSx}>{column.label}</Typography>
                                        <Box
                                            sx={{
                                                mt: 0.3,
                                                fontSize: '0.84rem',
                                                lineHeight: 1.45,
                                                fontFamily: column.align === 'right' || column.monospace ? MONO_FONT : 'inherit',
                                                fontVariantNumeric:
                                                    column.align === 'right' || column.monospace ? 'tabular-nums' : 'normal',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {renderColumnValue(column, row)}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        ) : (
            <TableContainer
                sx={(theme) => ({
                    borderRadius: INNER_RADIUS,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.9),
                    overflowX: 'auto',
                    overflowY: 'hidden',
                })}
            >
                <Table
                    size="small"
                    sx={(theme) => ({
                        minWidth: Math.max(columns.length * 116, 640),
                        tableLayout: 'auto',
                        '& thead th': {
                            py: compact ? 0.9 : 1.05,
                            px: compact ? 1 : 1.15,
                            borderBottom: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.95),
                            backgroundColor: theme.palette.mode === 'dark'
                                ? alpha('#0f172a', 0.82)
                                : alpha('#f8fafc', 0.98),
                            color: theme.palette.text.secondary,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                        },
                        '& tbody td': {
                            py: compact ? 0.9 : 1.05,
                            px: compact ? 1 : 1.15,
                            borderBottom: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.7),
                            fontSize: compact ? '0.78rem' : '0.82rem',
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap',
                        },
                        '& tbody tr:last-of-type td': {
                            borderBottom: 'none',
                        },
                    })}
                >
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.key} align={column.align || 'left'}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={`${title}-${index}`}>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        align={column.align || 'left'}
                                        sx={
                                            column.align === 'right' || column.monospace
                                                ? {
                                                      fontFamily: MONO_FONT,
                                                      fontVariantNumeric: 'tabular-nums',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {renderColumnValue(column, row)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    ) : (
        <Typography variant="body2" color="text.secondary">
            {emptyMessage}
        </Typography>
    );

    if (!framed) {
        return content;
    }

    return (
        <PanelFrame title={title} description={description} accent={accent}>
            {content}
        </PanelFrame>
    );
};
